'use client'

import { useGetBrandQuery } from '@/api/brands/queries/get-brand-query'
import { useGetBrandsQuery } from '@/api/brands/queries/get-brands-query'
import { BrandContext } from '@/features/brands/brand-context-definition'
import { buildBrandSegment, parseBrandUuidFromSegment } from '@/features/brands/brand-url-identifier'
import { LoadingScreen } from '@/features/loading/loading-screen'
import { IChildrenProps } from '@/utils/typings/children-props'
import { useQueryClient } from '@tanstack/react-query'
import { useParams, useRouter } from 'next/navigation'
import { UrlConfig } from '@/setup/routing/routing'
import { useCallback, useEffect, useRef } from 'react'

export { useBrandContext } from '@/features/brands/brand-context-definition'
export type { IBrandContext } from '@/features/brands/brand-context-definition'

type IBrandContextProviderProps = IChildrenProps & {
    renderFallback?: () => React.ReactNode
}

/**
 * Brand identity provider — UUID-canonical (Notion-style URLs).
 *
 * The `[brandSlug]` URL segment carries the brand UUID (`adidas-{uuid}` or
 * bare `{uuid}`); ONLY the UUID resolves the brand, the prefix is decoration.
 * `brandSlug` exposed on the context is that UUID — it keys every brand-scoped
 * query and API path, and it never changes for the lifetime of a brand. A
 * rename is therefore pure data: no URL rewrite, no query-key flip, no
 * remount, no reconciliation machinery.
 *
 * Navigation rules:
 *  - Only user intent navigates (`setBrand` from clicks).
 *  - ONE error redirect exists (brand 404 after retries → first brand),
 *    firing at most once per mount.
 *  - URL normalization is cosmetic (`history.replaceState`) for UUID-suffixed
 *    segments; legacy plain-slug links get one real replace at entry so the
 *    session's routing identity becomes the UUID.
 */
export const BrandContextProvider = ({ children, renderFallback }: IBrandContextProviderProps) => {
    const router = useRouter()
    const params = useParams()
    const queryClient = useQueryClient()
    const { data: brands } = useGetBrandsQuery()

    const rawSegment = params.brandSlug as string | undefined

    // CANONICAL IDENTITY — the UUID parsed from the URL segment. Legacy
    // plain-slug segments (old bookmarks, backend-generated links) resolve
    // through the brands list once it loads; until then the raw slug itself
    // is a valid API identifier (middleware accepts it), and the normalize
    // effect below upgrades the URL to canonical form as soon as the brand
    // is known.
    const parsedUuid = parseBrandUuidFromSegment(rawSegment)
    const legacySlugUuid =
        !parsedUuid && rawSegment ? (brands?.find((b) => b.slug === rawSegment)?.uuid ?? null) : null
    const brandIdentifier = parsedUuid ?? legacySlugUuid ?? rawSegment ?? brands?.[0]?.uuid ?? ''

    const { data: brand, error: brandError } = useGetBrandQuery(false, brandIdentifier, {
        // A just-created brand can briefly 404 while caches repopulate. Retry
        // transient 404s so the page recovers in place instead of falling
        // through to the first-brand redirect below.
        retry: (failureCount, error) => error?.status === 404 && failureCount < 3,
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
    })

    /**
     * SWITCH between brands — plain client-side navigation to the target's
     * canonical URL. Accepts any identifier (uuid, canonical segment, or
     * slug) and resolves it against the brands list.
     */
    const handleBrandChange = useCallback(
        (nextBrandIdentifier: string, mode: 'replace' | 'push' = 'push') => {
            if (!rawSegment) return

            const nextUuid = parseBrandUuidFromSegment(nextBrandIdentifier)
            const target = brands?.find(
                (b) =>
                    b.uuid.toLowerCase() === (nextUuid ?? nextBrandIdentifier.toLowerCase()) ||
                    b.slug === nextBrandIdentifier
            )

            const targetSegment = target ? buildBrandSegment(target) : nextBrandIdentifier
            // Already on this brand (compare by uuid when known, else by segment).
            const targetIdentity = target?.uuid.toLowerCase() ?? nextUuid ?? targetSegment
            if (targetIdentity === brandIdentifier.toLowerCase() || targetSegment === rawSegment) return

            const nextRoute = UrlConfig['/brands/[brandSlug]'].getUrl({ brandSlug: targetSegment })
            if (mode === 'replace') {
                router.replace(nextRoute)
            } else {
                router.push(nextRoute)
            }
        },
        [rawSegment, brands, brandIdentifier, router]
    )

    // On identity change, cancel every in-flight query scoped to the OLD
    // brand. React Query passes AbortSignal into every queryFn; cancelling
    // aborts those fetches with AbortError so any chained `.then` on the old
    // brand short-circuits into `.catch`. Primary defense against
    // stale-callback bugs during a brand switch. (Mutations are not covered
    // — use `useCurrentBrandGuard` at await-then-navigate callsites.)
    const previousIdentifierRef = useRef(brandIdentifier)
    useEffect(() => {
        const previousIdentifier = previousIdentifierRef.current
        previousIdentifierRef.current = brandIdentifier
        if (!previousIdentifier || previousIdentifier === brandIdentifier) return
        queryClient.cancelQueries({
            predicate: (q) => q.queryKey.includes(previousIdentifier),
        })
    }, [brandIdentifier, queryClient])

    // THE single error-redirect owner. Fires only on a DEFINITIVE server
    // verdict — never on empty/undefined data, and at most once per FAILING
    // IDENTITY (this provider lives in the persistent layout, so a plain
    // fire-once ref would disarm recovery for the whole SPA session):
    //  - 404: brand deleted, or a junk identifier
    //  - 403: the identifier resolves to ANOTHER org's brand (cross-org link,
    //    stale bookmark after a workspace switch)
    // The target is the first brand that ISN'T the failing one — the brands
    // list is recency-sorted, so brands[0] can be the just-deleted current
    // brand itself. With no alternative, fall back to /routing which
    // re-resolves brands fresh.
    const redirectedForIdentifierRef = useRef<string | null>(null)
    useEffect(() => {
        if (redirectedForIdentifierRef.current === brandIdentifier) return
        if ((brandError?.status === 404 || brandError?.status === 403) && brands) {
            redirectedForIdentifierRef.current = brandIdentifier
            const fallbackBrand = brands.find(
                (b) => b.uuid.toLowerCase() !== brandIdentifier.toLowerCase() && b.slug !== brandIdentifier
            )
            router.replace(
                fallbackBrand
                    ? UrlConfig['/brands/[brandSlug]'].getUrl({ brandSlug: buildBrandSegment(fallbackBrand) })
                    : UrlConfig['/routing'].getUrl()
            )
        }
    }, [brandError, brands, brandIdentifier, router])

    // URL NORMALIZATION. Two cases:
    //  - UUID-suffixed segment with a stale/missing prefix (brand renamed):
    //    identity is unchanged, so this is a COSMETIC address-bar fix via
    //    history.replaceState — no navigation, no remount, streams live on.
    //  - Legacy plain-slug segment (old bookmark / backend link): one real
    //    router.replace at entry so the canonical UUID becomes the routing
    //    identity for the rest of the session.
    useEffect(() => {
        if (!brand?.uuid || !rawSegment) return
        const canonical = buildBrandSegment(brand)
        if (rawSegment === canonical) return

        if (parsedUuid) {
            if (parsedUuid !== brand.uuid.toLowerCase()) return
            const currentUrl = new URL(window.location.href)
            const segments = currentUrl.pathname.split('/')
            const brandsIdx = segments.findIndex((s) => s === 'brands')
            if (brandsIdx === -1 || brandsIdx + 1 >= segments.length) return
            // Only touch the address bar if it still shows THIS brand's stale
            // segment (a previous run may have fixed it; a navigation may
            // have moved on).
            if (segments[brandsIdx + 1] === canonical) return
            if (parseBrandUuidFromSegment(segments[brandsIdx + 1]) !== brand.uuid.toLowerCase()) return
            segments[brandsIdx + 1] = canonical
            window.history.replaceState(
                window.history.state,
                '',
                segments.join('/') + currentUrl.search + currentUrl.hash
            )
        } else {
            if (brand.slug !== rawSegment) return
            const currentUrl = new URL(window.location.href)
            const segments = currentUrl.pathname.split('/')
            const brandsIdx = segments.findIndex((s) => s === 'brands')
            if (brandsIdx === -1 || brandsIdx + 1 >= segments.length) return
            segments[brandsIdx + 1] = canonical
            router.replace(segments.join('/') + currentUrl.search + currentUrl.hash)
        }
    }, [brand, rawSegment, parsedUuid, router])

    if (!brand && renderFallback) {
        return renderFallback?.() || <LoadingScreen />
    }

    return (
        <BrandContext.Provider
            value={{
                brand,
                setBrand: handleBrandChange,
                brandSlug: brandIdentifier,
            }}
        >
            {children}
        </BrandContext.Provider>
    )
}
